import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import SwipeableViews from 'react-swipeable-views';

import AssessmentIcon from '@material-ui/icons/Assessment';
import ListAltIcon from '@material-ui/icons/ListAlt';
import RefreshIcon from '@material-ui/icons/Refresh';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import FormComponent from '../../components/FormComponent';
import FMPHeadline from '../../components/ui/FMPHeadline';
import FMPStatusIcon from '../../components/ui/FMPStatusIcon';
import FMPCheckbox from '../../components/pureForm/FMPCheckbox';
import FMPMonthYearPicker from '../../components/pureForm/FMPMonthYearPicker';
import FMPSelect from '../../components/pureForm/FMPSelect';
import EnhancedTable from '../../components/table/EnhancedTable';
import InventoryBalanceStatusContainer from '../../containers/inventory/InventoryBalanceStatus';
import { getStatus, searchBalances, searchQueue } from '../../modules/inventory/inventoryBalanceStatus';
import { getSharedStyles } from '../../themes';

const QUEUE_STATUS_COLORS = {
	pending: '#FF9800',
	processing: '#2196F3',
	done: '#4CAF50',
	error: '#f44336',
};

const styles = (theme) => ({
	...getSharedStyles(theme),
	//- Copied from InventoryReports.js -- these five keys are not extracted into a shared
	//- component anywhere, and extracting them is not part of this work.
	summaryBar: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		padding: '10px 16px',
		marginBottom: 12,
		background: '#fafafa',
		border: '1px solid #eee',
		borderRadius: 4,
	},
	summaryBarStat: {
		marginRight: 28,
	},
	summaryBarLabel: {
		fontSize: 11,
		textTransform: 'uppercase',
		letterSpacing: 1,
		color: '#888',
	},
	summaryBarValue: {
		fontSize: 18,
		fontWeight: 500,
	},
	summaryBarDivider: {
		width: 1,
		alignSelf: 'stretch',
		background: '#ddd',
		marginRight: 28,
	},
	healthReasons: {
		background: '#FFF8E1',
		border: '1px solid #FFE082',
		borderRadius: 4,
		padding: '8px 10px',
		marginBottom: 12,
		fontSize: 13,
	},
	subValue: {
		fontSize: 11,
		color: '#888',
	},
	truncated: {
		display: 'inline-block',
		maxWidth: 260,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		verticalAlign: 'middle',
	},
	resultsHint: {
		fontSize: 12,
		color: '#888',
		marginBottom: 6,
	},
});

//- Defined locally in every page in this app that uses tabs.
function TabContainer({ children, dir }) {
	return (
		<Typography component="div" dir={dir} style={{ padding: 8 * 2 }}>
			{children}
		</Typography>
	);
}
TabContainer.propTypes = {
	children: PropTypes.node.isRequired,
	dir: PropTypes.string.isRequired,
};

const balanceColumns = [
	{ sortId: 'periodSort', displayId: 'periodDisplay', label: 'Period' },
	{ sortId: 'terminalName', displayId: 'terminalDisplay', label: 'Terminal' },
	{ sortId: 'supplierName', displayId: 'supplierDisplay', label: 'Supplier' },
	{ sortId: 'productType', displayId: 'productDisplay', label: 'Product' },
	{ sortId: 'beginningInventoryGallons', displayId: 'beginningDisplay', label: 'Beginning', numeric: true },
	{ sortId: 'purchasedGallons', displayId: 'purchasedDisplay', label: 'Purchased', numeric: true },
	{ sortId: 'soldGallons', displayId: 'soldDisplay', label: 'Sold', numeric: true },
	{ sortId: 'gainLossGallons', displayId: 'gainLossDisplay', label: 'Gain/Loss', numeric: true },
	{ sortId: 'endingInventoryGallons', displayId: 'endingDisplay', label: 'Ending', numeric: true },
	{ sortId: 'railcarCount', displayId: 'railcarCount', label: 'Railcars', numeric: true },
	{ sortId: 'deliveryCount', displayId: 'deliveryCount', label: 'Deliveries', numeric: true },
	{ sortId: 'dateComputed', displayId: 'dateComputedDisplay', label: 'Computed' },
	{ sortId: 'lastTriggerType', displayId: 'triggerDisplay', label: 'Last Trigger' },
	{ sortId: 'warningCount', displayId: 'warningsDisplay', label: 'Warnings' },
];

const queueColumns = [
	{ sortId: 'inventoryBalanceRecalcQueueId', displayId: 'queueIdDisplay', label: 'ID', numeric: true },
	{ sortId: 'dateCreated', displayId: 'dateCreatedDisplay', label: 'Enqueued' },
	{ sortId: 'status', displayId: 'statusDisplay', label: 'Status' },
	{ sortId: 'terminalName', displayId: 'terminalDisplay', label: 'Terminal' },
	{ sortId: 'periodSort', displayId: 'periodDisplay', label: 'Period' },
	{ sortId: 'triggerType', displayId: 'triggerDisplay', label: 'Trigger' },
	{ sortId: 'attemptCount', displayId: 'attemptCountDisplay', label: 'Attempts', numeric: true },
	{ sortId: 'dateProcessed', displayId: 'dateProcessedDisplay', label: 'Processed' },
	{ sortId: 'lastError', displayId: 'lastErrorDisplay', label: 'Error' },
];

const limitOptions = [
	{ value: 100, display: '100 rows' },
	{ value: 250, display: '250 rows' },
	{ value: 500, display: '500 rows' },
	{ value: 1000, display: '1,000 rows' },
];

function formatGallons(value) {
	if (value == null) {
		return '-';
	}
	return Math.round(value).toLocaleString();
}

function periodLabel(periodYear, periodMonth) {
	return moment(`${periodYear}-${periodMonth}-01`, 'YYYY-M-DD').format('MMM YYYY');
}

class InventoryBalanceSync extends FormComponent {
	constructor(props) {
		super(props);
		this.state = {
			tabIndex: 0,
			status: undefined,
			isLoading: true,

			balanceRows: undefined,
			balanceTotal: 0,
			isLoadingBalances: false,
			yearMonth: '',
			onlyWithWarnings: false,

			queueRows: undefined,
			queueTotal: 0,
			isLoadingQueue: false,
			queueStatus: '',
			queueLimit: 250,
		};
	}

	componentDidMount() {
		this.loadStatus();
		this.loadBalances();
	}

	showError = (err) => {
		this.props.showErrors(_.isArray(err) ? err : [err]);
	};

	loadStatus = () => {
		getStatus({
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoading: false });
					return this.showError(err);
				}
				this.setState({ status: r, isLoading: false });
			},
		});
	};

	loadBalances = () => {
		const { yearMonth, onlyWithWarnings } = this.state;
		const yearMonthMoment = yearMonth ? moment(yearMonth, 'YYYY-MM') : null;

		this.setState({ isLoadingBalances: true });
		searchBalances({
			periodYear: yearMonthMoment ? yearMonthMoment.year() : undefined,
			periodMonth: yearMonthMoment ? yearMonthMoment.month() + 1 : undefined,
			onlyWithWarnings: onlyWithWarnings || undefined,
			limit: 1000,
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoadingBalances: false });
					return this.showError(err);
				}
				this.setState({
					isLoadingBalances: false,
					balanceRows: this.buildBalanceRows(r),
					balanceTotal: r.total,
				});
			},
		});
	};

	loadQueue = () => {
		const { queueStatus, queueLimit } = this.state;

		this.setState({ isLoadingQueue: true });
		searchQueue({
			statuses: queueStatus ? [queueStatus] : undefined,
			limit: queueLimit,
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoadingQueue: false });
					return this.showError(err);
				}
				this.setState({
					isLoadingQueue: false,
					queueRows: this.buildQueueRows(r),
					queueTotal: r.total,
				});
			},
		});
	};

	buildBalanceRows = (result) => {
		const { classes } = this.props;
		const collections = result.collections || {};
		const terminalMap = _.keyBy(collections.terminalCollection || [], 'terminalId');
		const supplierMap = _.keyBy(collections.supplierCollection || [], 'supplierId');
		const staleCutoff = moment().subtract(36, 'hours');
		const currentMonth = moment().format('YYYY-MM');

		return (collections.inventoryPeriodBalanceCollection || []).map((row) => {
			const warnings = row.calculationWarningList || [];
			const isCurrentMonth =
				`${row.periodYear}-${String(row.periodMonth).padStart(2, '0')}` === currentMonth;
			const isStale = isCurrentMonth && moment(row.dateComputed).isBefore(staleCutoff);

			return {
				key: `b${row.inventoryPeriodBalanceId}`,

				//- Sort keys are the raw values; only the *Display fields are formatted.
				periodSort: row.periodYear * 100 + row.periodMonth,
				periodDisplay: periodLabel(row.periodYear, row.periodMonth),

				terminalName: _.get(terminalMap, [row.terminalId, 'name'], `Terminal ${row.terminalId}`),
				terminalDisplay: _.get(terminalMap, [row.terminalId, 'name'], `Terminal ${row.terminalId}`),

				supplierName: _.get(supplierMap, [row.supplierForTerminalId, 'name'], 'Unresolved'),
				supplierDisplay: _.get(supplierMap, [row.supplierForTerminalId, 'name'], 'Unresolved'),

				productType: row.productType,
				productDisplay: (row.productType || '').toUpperCase(),

				beginningInventoryGallons: row.beginningInventoryGallons,
				beginningDisplay: formatGallons(row.beginningInventoryGallons),
				purchasedGallons: row.purchasedGallons,
				purchasedDisplay: formatGallons(row.purchasedGallons),
				soldGallons: row.soldGallons,
				soldDisplay: formatGallons(row.soldGallons),
				gainLossGallons: row.gainLossGallons,
				gainLossDisplay: (
					<span style={{ color: row.gainLossGallons < 0 ? '#f44336' : undefined }}>
						{formatGallons(row.gainLossGallons)}
					</span>
				),
				endingInventoryGallons: row.endingInventoryGallons,
				endingDisplay: (
					<strong style={{ color: row.endingInventoryGallons < 0 ? '#f44336' : undefined }}>
						{formatGallons(row.endingInventoryGallons)}
					</strong>
				),

				railcarCount: row.railcarCount,
				deliveryCount: row.deliveryCount,

				dateComputed: row.dateComputed,
				dateComputedDisplay: (
					<span>
						{moment(row.dateComputed).format('M/D h:mm a')}
						{isStale && (
							<Tooltip
								title={'This is the current month but it has not been recomputed in over 36 hours'}
							>
								<WarningRoundedIcon
									style={{ color: '#FF9800', fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }}
								/>
							</Tooltip>
						)}
					</span>
				),

				lastTriggerType: row.lastTriggerType,
				triggerDisplay: (
					<Tooltip title={row.lastTriggerNote || ''}>
						<div>
							<div>{row.lastTriggerType || '-'}</div>
							{row.lastTriggerNote && (
								<div className={classes.subValue}>
									<span className={classes.truncated}>{row.lastTriggerNote}</span>
								</div>
							)}
						</div>
					</Tooltip>
				),

				warningCount: warnings.length,
				//- A count behind a tooltip, not inline text: calculationWarnings is a joined
				//- blob that would wreck the row height.
				warningsDisplay: _.isEmpty(warnings) ? (
					'-'
				) : (
					<Tooltip
						title={
							<div>
								{warnings.map((warning) => (
									<div key={warning}>{warning}</div>
								))}
							</div>
						}
					>
						<span style={{ color: '#FF9800', whiteSpace: 'nowrap' }}>
							<WarningRoundedIcon style={{ fontSize: 16, verticalAlign: 'middle' }} /> {warnings.length}
						</span>
					</Tooltip>
				),
			};
		});
	};

	buildQueueRows = (result) => {
		const { classes } = this.props;
		const collections = result.collections || {};
		const terminalMap = _.keyBy(collections.terminalCollection || [], 'terminalId');

		return (collections.inventoryBalanceRecalcQueueCollection || []).map((row) => {
			let processedDisplay = '-';
			if (row.dateProcessed) {
				processedDisplay = moment(row.dateProcessed).format('M/D h:mm a');
			} else if (row.status === 'processing' && row.claimedAgeMinutes != null) {
				//- Surfacing the claim age here means a stuck claim is visible in the row itself,
				//- not only in the summary.
				processedDisplay = `claimed ${row.claimedAgeMinutes}m ago`;
			}

			return {
				key: `q${row.inventoryBalanceRecalcQueueId}`,

				inventoryBalanceRecalcQueueId: row.inventoryBalanceRecalcQueueId,
				queueIdDisplay: row.inventoryBalanceRecalcQueueId,

				dateCreated: row.dateCreated,
				dateCreatedDisplay: moment(row.dateCreated).format('M/D h:mm a'),

				status: row.status,
				statusDisplay: (
					<span style={{ whiteSpace: 'nowrap' }}>
						<span
							style={{
								display: 'inline-block',
								width: 8,
								height: 8,
								borderRadius: '50%',
								marginRight: 6,
								background: QUEUE_STATUS_COLORS[row.status] || '#999',
							}}
						/>
						{row.status}
					</span>
				),

				terminalName: _.get(terminalMap, [row.terminalId, 'name'], `Terminal ${row.terminalId}`),
				terminalDisplay: _.get(terminalMap, [row.terminalId, 'name'], `Terminal ${row.terminalId}`),

				periodSort: row.periodYear * 100 + row.periodMonth,
				periodDisplay: periodLabel(row.periodYear, row.periodMonth),

				triggerType: row.triggerType,
				triggerDisplay: (
					<Tooltip title={row.triggerNote || ''}>
						<div>
							<div>{row.triggerType || '-'}</div>
							{row.triggerNote && (
								<div className={classes.subValue}>
									<span className={classes.truncated}>{row.triggerNote}</span>
								</div>
							)}
						</div>
					</Tooltip>
				),

				attemptCount: row.attemptCount,
				attemptCountDisplay: (
					<span style={{ color: row.attemptCount >= 5 ? '#f44336' : undefined }}>
						{row.attemptCount}
					</span>
				),

				dateProcessed: row.dateProcessed,
				dateProcessedDisplay: processedDisplay,

				lastError: row.lastError,
				lastErrorDisplay: row.lastError ? (
					<Tooltip title={row.lastError}>
						<span style={{ color: '#f44336' }} className={classes.truncated}>
							{row.lastError}
						</span>
					</Tooltip>
				) : (
					'-'
				),
			};
		});
	};

	handleTabChange = (event, tabIndex) => {
		this.setState({ tabIndex });
		//- Lazy: the queue is not fetched until someone actually looks at it.
		if (tabIndex === 1 && !this.state.queueRows) {
			this.loadQueue();
		}
	};

	handleChangeTabIndex = (tabIndex) => {
		this.handleTabChange(null, tabIndex);
	};

	handleRefresh = () => {
		this.loadStatus();
		this.loadBalances();
		if (this.state.queueRows) {
			this.loadQueue();
		}
	};

	renderSummaryBar = () => {
		const { classes } = this.props;
		const { status } = this.state;
		if (!status) {
			return null;
		}

		const stat = (label, value) => (
			<div className={classes.summaryBarStat} key={label}>
				<div className={classes.summaryBarLabel}>{label}</div>
				<div className={classes.summaryBarValue}>{value}</div>
			</div>
		);

		return (
			<div className={classes.summaryBar}>
				<div className={classes.summaryBarStat}>
					<div className={classes.summaryBarLabel}>Health</div>
					<div className={classes.summaryBarValue}>
						<FMPStatusIcon status={status.healthSeverity} title={status.health} /> {status.health}
					</div>
				</div>
				<div className={classes.summaryBarDivider} />
				{stat(
					'Last Run',
					status.lastRun.lastRunAt
						? moment(status.lastRun.lastRunAt).format('M/D h:mm a')
						: 'never'
				)}
				{stat('Pending', status.queue.pendingCount.toLocaleString())}
				{stat('Errors (7d)', status.queue.errorCountLast7Days.toLocaleString())}
				<div className={classes.summaryBarDivider} />
				{stat('Periods', status.coverage.periodCount.toLocaleString())}
				{stat('Terminals', status.coverage.terminalCount.toLocaleString())}
			</div>
		);
	};

	renderBalancesTab = () => {
		const { classes } = this.props;
		const { balanceRows, balanceTotal, isLoadingBalances } = this.state;

		return (
			<>
				<Grid container spacing={3}>
					<FMPMonthYearPicker
						{...this.setForm('yearMonth', 'Month / Year')}
						md={3}
						helpText={'Leave blank for every period'}
					/>
					<FMPCheckbox
						{...this.setForm('onlyWithWarnings', 'Only rows with warnings')}
						md={3}
					/>
					<Grid item xs={12} md={3} style={{ marginTop: 18 }}>
						<Button variant="contained" color="primary" onClick={this.loadBalances}>
							Apply
						</Button>
					</Grid>
				</Grid>

				{isLoadingBalances && <div className={classes.fetching}>Loading balances...</div>}
				{!isLoadingBalances && _.isEmpty(balanceRows) && (
					<div className={classes.resultsHint}>No persisted balances match these filters.</div>
				)}
				{!isLoadingBalances && !_.isEmpty(balanceRows) && (
					<>
						{balanceRows.length < balanceTotal && (
							<div className={classes.resultsHint}>
								Showing {balanceRows.length.toLocaleString()} of {balanceTotal.toLocaleString()} —
								narrow the filters to see the rest.
							</div>
						)}
						<EnhancedTable
							isDense={true}
							columns={balanceColumns}
							rows={balanceRows}
							defaultOrder={'desc'}
							defaultOrderBy={'periodSort'}
						/>
					</>
				)}
			</>
		);
	};

	renderQueueTab = () => {
		const { classes } = this.props;
		const { queueRows, queueTotal, isLoadingQueue, status } = this.state;

		const triggerOptions = _.get(status, 'queue.triggerSummary', []).map((t) => ({
			value: t.triggerType,
			display: `${t.triggerType} (${t.rowCount})`,
		}));

		return (
			<>
				<Grid container spacing={3}>
					<FMPSelect
						{...this.setForm('queueStatus', 'Status')}
						md={3}
						options={[
							{ value: '', display: 'All statuses' },
							{ value: 'pending', display: 'Pending' },
							{ value: 'processing', display: 'Processing' },
							{ value: 'done', display: 'Done' },
							{ value: 'error', display: 'Error' },
						]}
					/>
					<FMPSelect
						{...this.setForm('queueLimit', 'Show')}
						md={3}
						options={limitOptions}
					/>
					<Grid item xs={12} md={3} style={{ marginTop: 18 }}>
						<Button variant="contained" color="primary" onClick={this.loadQueue}>
							Apply
						</Button>
					</Grid>
				</Grid>

				{!_.isEmpty(triggerOptions) && (
					<div className={classes.resultsHint}>
						Triggers in the last 24 hours: {triggerOptions.map((t) => t.display).join(', ')}
					</div>
				)}

				{isLoadingQueue && <div className={classes.fetching}>Loading queue activity...</div>}
				{!isLoadingQueue && _.isEmpty(queueRows) && (
					<div className={classes.resultsHint}>No queue activity matches these filters.</div>
				)}
				{!isLoadingQueue && !_.isEmpty(queueRows) && (
					<>
						{queueRows.length < queueTotal && (
							<div className={classes.resultsHint}>
								Showing {queueRows.length.toLocaleString()} of {queueTotal.toLocaleString()} — raise
								the row limit or narrow the filters to see the rest.
							</div>
						)}
						<EnhancedTable
							isDense={true}
							columns={queueColumns}
							rows={queueRows}
							defaultOrder={'desc'}
							defaultOrderBy={'inventoryBalanceRecalcQueueId'}
						/>
					</>
				)}
			</>
		);
	};

	render() {
		const { classes, theme, canRwContracts } = this.props;
		const { tabIndex, status, isLoading } = this.state;

		if (!canRwContracts) {
			return <div>You do not have permission to view this page.</div>;
		}

		return (
			<div>
				<FMPHeadline label="Inventory Balance Sync" />

				{isLoading && <div className={classes.fetching}>Loading status...</div>}

				{status && (
					<>
						{this.renderSummaryBar()}
						{!_.isEmpty(status.healthReasons) && (
							<div className={classes.healthReasons}>
								{status.healthReasons.map((reason) => (
									<div key={reason}>{reason}</div>
								))}
							</div>
						)}
						<div style={{ marginBottom: 12 }}>
							<Button size="small" startIcon={<RefreshIcon />} onClick={this.handleRefresh}>
								Refresh
							</Button>
						</div>
					</>
				)}

				<AppBar position="static" color="default">
					<Tabs
						value={tabIndex}
						onChange={this.handleTabChange}
						indicatorColor="primary"
						textColor="primary"
						variant="fullWidth"
						centered
					>
						<Tab label="Persisted Balances" icon={<AssessmentIcon />} />
						<Tab label="Recent Queue Activity" icon={<ListAltIcon />} />
					</Tabs>
				</AppBar>

				<Paper className={classes.paper}>
					<SwipeableViews
						axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
						index={tabIndex}
						onChangeIndex={this.handleChangeTabIndex}
					>
						<TabContainer dir={theme.direction}>{this.renderBalancesTab()}</TabContainer>
						<TabContainer dir={theme.direction}>{this.renderQueueTab()}</TabContainer>
					</SwipeableViews>
				</Paper>
			</div>
		);
	}
}

InventoryBalanceSync.propTypes = {
	classes: PropTypes.object.isRequired,
	theme: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
	canRwContracts: PropTypes.bool,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	InventoryBalanceStatusContainer,
])(InventoryBalanceSync);
